<script lang="ts" setup>
	import useNotificationApi from '~/services/apis/notifications'

	const props = defineProps<{
		wrap?: boolean
		limit?: number
	}>()
	const { getActivities } = useNotificationApi()
	const { timeDifference } = useRelativeTime()

	const { data, isLoading } = useApiLoader(
		async () => {
			return await getActivities({
				limit: props.limit
			})
		},
		{ lazyLoad: true }
	)
</script>

<template>
	<div>
		<template v-if="isLoading">
			<a-skeleton v-for="item in 3" :key="'activities-' + item" avatar active loading class="mb-5" />
		</template>
		<slot v-else-if="!data?.data.items?.length" name="empty-placeholder">
			<shared-empty-state
				title="No Activity"
				description="Your activities will appear here when you have any"
				class="mt-12 space-y-2"
				title-class="font-semibold  text-gray-600"
				desc-class="px-5 max-w-[356px]"
			>
				<template #icon>
					<div class="py-5">
						<lazy-tools-icon icon="clipboard-document-2" :height="120" :width="120" dir="documents" />
					</div>
				</template>
			</shared-empty-state>
		</slot>

		<ul v-else>
			<li v-for="(item, index) in data.data.items" :key="index" class="flex items-center gap-3 pb-5">
				<div class="avatar font-bold uppercase">
					{{ item?.title?.[0] ?? 'N/A' }}
				</div>

				<div class="w-full flex-1 space-y-2">
					<div class="flex items-center justify-between">
						<h4 class="text-sm font-semibold capitalize text-gray-600">
							{{ String(item?.title).replace(/_/g, ' ') }}
						</h4>
						<span class="text-xs text-gray-500">{{ timeDifference(new Date(item.created_at)) }}</span>
					</div>

					<a-tooltip>
						<p
							class="w-full max-w-[320px] overflow-hidden text-ellipsis text-[11px] capitalize text-gray-500"
							:class="{
								'sm:whitespace-nowrap': wrap
							}"
						>
							{{ String(item.description).replace(/_/g, ' ') }}
						</p>
						<template #title>
							<div class="capitalize">
								{{ String(item.description).replace(/_/g, ' ') }}
							</div>
						</template>
					</a-tooltip>
				</div>
			</li>
		</ul>
	</div>
</template>

<style scoped>
	.avatar {
		@apply relative flex h-[47px] w-[47px] flex-none items-center justify-center rounded-full border bg-[#D0D5DD] text-white before:absolute before:-right-0 before:-top-1 before:h-[17px] before:w-[17px] before:rounded-full before:border-2 before:border-white before:bg-[#D0D5DD];
	}
</style>
